import React, { useState, useEffect } from "react";
import { MapPin } from 'lucide-react';

type Feature = {
  properties: {
    label: string;
    context: string;
    city: string;
    postcode: string;
  };
};

type Props = {
  onSelect: (adresse: string) => void;
  placeholder?: string;
  className?: string;
  value?: string;
};

const AutocompleteAdresse: React.FC<Props> = ({ 
  onSelect, 
  placeholder = "Entrez l'adresse de votre bien",
  className = "",
  value = ""
}) => {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<Feature[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (query.length < 4) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    const timeoutId = setTimeout(() => {
      fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=50`)
        .then((res) => res.json())
        .then((data) => {
          const filteredFeatures = (data.features || [])
            .filter((feature: Feature) => feature.properties.postcode.startsWith('06'))
            .slice(0, 10);
          setSuggestions(filteredFeatures);
        })
        .catch((error) => {
          console.error('Error fetching addresses:', error);
          setSuggestions([]);
        })
        .finally(() => setIsLoading(false));
    }, 150);

    return () => clearTimeout(timeoutId);
  }, [query]);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-6 w-6 sm:h-7 sm:w-7" />
        <input
          type="text"
          className="block w-full pl-12 sm:pl-14 pr-4 py-4 sm:py-5 bg-white/95 rounded-xl 
            text-gray-900 placeholder-gray-500 text-base sm:text-xl 
            focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent 
            border border-gray-200 shadow-sm
            transition-all duration-300"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        {isLoading && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
          </div>
        )}
      </div>

      {suggestions.length > 0 && (
        <>
          <div 
            className="fixed inset-0 bg-black/20" 
            style={{ zIndex: 9998 }}
            onClick={() => setSuggestions([])} 
          />
          <ul 
            className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.12)] 
              border border-gray-100 max-h-[300px] overflow-y-auto"
            style={{ zIndex: 9999 }}
          >
            {suggestions.map((feature, idx) => (
              <li
                key={idx}
                className="border-b border-gray-100 last:border-none"
                onClick={() => {
                  setQuery(feature.properties.label);
                  setSuggestions([]);
                  onSelect(feature.properties.label);
                }}
              >
                <div className="flex items-start p-3 sm:p-4 hover:bg-indigo-50 cursor-pointer transition-colors">
                  <MapPin className="h-5 w-5 text-indigo-600 mt-1 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm sm:text-base text-gray-900 font-medium">{feature.properties.label}</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
                      {feature.properties.postcode} {feature.properties.city}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default AutocompleteAdresse;